import moment from 'moment';
import { SnapshotModel, getModelFromSnapshot } from '..';
import { Collections } from '../../constants';
import { InvoiceStatus } from '../../enums';
import {
  Invoice,
  OrderInvoice,
  PaymentGateway,
  PublicNoticeInvoice,
  isOrderInvoiceData,
  isPublicNoticeInvoiceData
} from '../../types/invoices';
import { UserNoticeModel } from './userNoticeModel';
import {
  ResponseOrColumnError,
  ResponseOrError,
  wrapError,
  wrapSuccess
} from '../../types/responses';
import { getErrorReporter } from '../../utils/errors';
import { ColumnService } from '../../services/directory';
import { OrderModel } from './orderModel';
import {
  BadRequestError,
  ConflictError,
  NotFoundError,
  wrapErrorAsColumnError
} from '../../errors/ColumnErrors';
import { safeAsync, safeGetOrThrow } from '../../safeWrappers';
import { isPaidDirectToPublisher } from '../../utils/invoices';
import {
  getOrganizationGatewayAndAccountCode,
  PaymentGatewayAndAccountCode
} from '../../billing/gateways';
import { safeUpdateModel } from '../safeHandlers';
import { ETransaction } from '../../types';

export const INVOICE_STATUSES_UNPAID = [
  InvoiceStatus.unpaid.value,
  InvoiceStatus.payment_failed.value,
  InvoiceStatus.draft.value
];

/**
 * We lump partial refund in here because we currently treat the invoice as "good as paid" in
 * integrations. That status is essentially used to track when the invoice can hold no more
 * refunds because before invoice transactions that was the limitation. Reassess this status
 * when we support multiple partial refunds.
 */
export const INVOICE_STATUSES_FUNDS_RECEIVED = [
  InvoiceStatus.paid.value,
  InvoiceStatus.partially_refunded.value
];

export const INVOICE_STATUSES_FUNDS_PENDING = [
  InvoiceStatus.initiated.value,
  InvoiceStatus.authorized.value
];

export const INVOICE_STATUSES_PAYMENT_OR_PARTIAL_REFUND = [
  ...INVOICE_STATUSES_FUNDS_RECEIVED,
  ...INVOICE_STATUSES_FUNDS_PENDING
];

/**
 * Closed invoices are non-draft invoices in Stripe that are no longer mutable
 * This concept is used in bulk invoices and some integrations to guard a sync
 */
export const INVOICE_STATUSES_CLOSED = [
  ...INVOICE_STATUSES_FUNDS_RECEIVED,
  InvoiceStatus.refunded.value
];

/**
 * This model supports a generic type T that extends Invoice, but defaults to PublicNoticeInvoice
 * if no type is provided. This allows us to override cases where this is a different invoice type.
 * Once the discriminated type 'Invoice' is fully implemented, we will no longer need a default here.
 */
export class InvoiceModel<
  T extends Invoice = PublicNoticeInvoice
> extends SnapshotModel<T, typeof Collections.invoices> {
  get type() {
    return Collections.invoices;
  }

  public async getNotice(): Promise<ResponseOrError<UserNoticeModel>> {
    if (!this.isPublicNoticeInvoice()) {
      const err = new Error('Invoice is not a public notice invoice');
      getErrorReporter().logAndCaptureError(
        ColumnService.ORDER_MANAGEMENT,
        err,
        'Cannot get notice for a non-public notice invoice',
        {
          invoiceId: this.id
        }
      );
      return wrapError(err);
    }
    const { response: notice, error: noticeError } = await safeGetOrThrow(
      this.ctx.userNoticesRef().doc(this.modelData.noticeId)
    );
    if (noticeError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.DATABASE,
        noticeError,
        'Unable to get notice for invoice',
        {
          noticeId: this.modelData.noticeId,
          invoiceId: this.id
        }
      );
      return wrapError(noticeError);
    }
    return wrapSuccess(getModelFromSnapshot(UserNoticeModel, this.ctx, notice));
  }

  public async getOrder(): Promise<ResponseOrColumnError<OrderModel>> {
    if (!this.isOrderInvoice()) {
      const err = new Error('Invoice is not an order invoice');
      getErrorReporter().logAndCaptureError(
        ColumnService.ORDER_MANAGEMENT,
        err,
        'Cannot get order for a non-order invoice',
        {
          invoiceId: this.id
        }
      );
      return wrapErrorAsColumnError(err, BadRequestError);
    }
    const { response: orderSnap, error: getOrderError } = await safeGetOrThrow(
      this.modelData.order
    );
    if (getOrderError || !orderSnap) {
      getErrorReporter().logAndCaptureError(
        ColumnService.ORDER_MANAGEMENT,
        getOrderError,
        'Unable to get order for invoice',
        {
          orderId: this.modelData.order.id,
          invoiceId: this.id
        }
      );
      return wrapErrorAsColumnError(getOrderError, NotFoundError);
    }

    return wrapSuccess(getModelFromSnapshot(OrderModel, this.ctx, orderSnap));
  }

  public isFreeInvoice() {
    return this.modelData.pricing.totalInCents === 0;
  }

  public isOrderInvoice(): this is InvoiceModel<OrderInvoice> {
    return isOrderInvoiceData(this.modelData);
  }

  public isPublicNoticeInvoice(): this is InvoiceModel<PublicNoticeInvoice> {
    return isPublicNoticeInvoiceData(this.modelData);
  }

  public isPaid(): boolean {
    return this.modelData.status === InvoiceStatus.paid.value;
  }

  public isPartiallyRefunded(): boolean {
    return this.modelData.status === InvoiceStatus.partially_refunded.value;
  }

  /**
   * Whether this invoice has been voided and refunded, fully or partially.
   * Integrations treat that money as gone rather than as a repriced balance.
   */
  public isVoidedRefund(): boolean {
    return (
      !!this.modelData.void &&
      (this.modelData.status === InvoiceStatus.refunded.value ||
        this.modelData.status === InvoiceStatus.partially_refunded.value)
    );
  }

  /**
   * Whether this invoice's money was collected through Stripe. False until the
   * invoice is paid: `paid_outside_stripe` is only written at payment time.
   */
  public isPaidThroughStripe(): boolean {
    const { finalized_at, paid_outside_stripe, invoiceOutsideColumn } =
      this.modelData;
    return !!finalized_at && !paid_outside_stripe && !invoiceOutsideColumn;
  }

  public isPastDue() {
    const { due_date } = this.modelData;
    const currentDate = moment();
    const dueDate = moment(due_date * 1000);
    return moment(dueDate).isBefore(currentDate);
  }

  public isUnpaid() {
    return (
      INVOICE_STATUSES_UNPAID.includes(this.modelData.status) &&
      !this.modelData.void
    );
  }

  public isRefunded() {
    return this.modelData.status === InvoiceStatus.refunded.value;
  }

  public isVoided() {
    return !!this.modelData.void;
  }

  public isUncollectible() {
    return this.modelData.status === InvoiceStatus.uncollectible.value;
  }

  /**
   * In-app only: call once Stripe has the invoice as uncollectible. The status check runs inside
   * the transaction so a payment landing concurrently is never overwritten.
   */
  public async markUncollectible(
    markedUncollectibleAt: Date
  ): Promise<ResponseOrColumnError<void>> {
    const [transactionError] = await safeAsync(() =>
      this.ctx.runTransaction(async (tx: ETransaction) => {
        const invoiceRef = this.ctx.invoicesRef<Invoice>().doc(this.id);
        const current = (await tx.get(invoiceRef)).data();
        if (!current) {
          throw new NotFoundError('Invoice not found');
        }
        if (current.status === InvoiceStatus.uncollectible.value) {
          return;
        }
        if (!INVOICE_STATUSES_UNPAID.includes(current.status) || current.void) {
          throw new ConflictError(
            `Invoice in status ${
              InvoiceStatus.by_value(current.status)?.label ?? current.status
            }${current.void ? ' (void)' : ''} cannot be marked uncollectible`
          );
        }
        tx.update(invoiceRef, {
          status: InvoiceStatus.uncollectible.value,
          marked_uncollectible_at: markedUncollectibleAt
        });
      })
    )();
    if (transactionError) {
      return wrapErrorAsColumnError(transactionError);
    }
    const [refreshError] = await safeAsync(() => this.refreshData())();
    if (refreshError) {
      return wrapErrorAsColumnError(refreshError);
    }
    return wrapSuccess(undefined);
  }

  public getStatusLabel() {
    return InvoiceStatus.by_value(this.modelData.status)?.label ?? 'Unknown';
  }

  public paymentReceived() {
    // TODO(goodpaul): Posisbly move 'refunded' into STATUS_FUNDS_RECEIVED
    const isRefunded = InvoiceStatus.refunded.value === this.modelData.status;
    return (
      (INVOICE_STATUSES_FUNDS_RECEIVED.includes(this.modelData.status) ||
        isRefunded) &&
      !this.modelData.void
    );
  }

  public paymentPending() {
    return (
      INVOICE_STATUSES_FUNDS_PENDING.includes(this.modelData.status) &&
      !this.modelData.void
    );
  }

  public paymentMethodMissing() {
    return (
      !this.modelData.paymentMethod ||
      this.modelData.paymentMethod === 'unknown'
    );
  }

  /**
   * The publisher has taken a cash or check payment from the customer. The payment is processed as paid out of platform in Stripe and not in Payway or Elavon.
   */
  public isPaidDirectToPublisher(): boolean {
    return isPaidDirectToPublisher(this);
  }

  public canRefundBulkPayment(): boolean {
    return (
      this.modelData.paymentMethod === 'bulk-payment' &&
      !!this.modelData.bulkStripePaymentIntentId &&
      this.paymentReceived()
    );
  }

  /**
   * Fast (non-async) accessor for gateway info if already populated on the invoice.
   * Returns null if gateway data has not yet been calculated/persisted.
   */
  public getPaymentGatewayInfo(): PaymentGatewayAndAccountCode | null {
    if (!this.modelData.paymentGateway) {
      return null;
    }
    if (this.modelData.paymentGateway === PaymentGateway.PAYWAY) {
      if (this.modelData.paymentAccountCode) {
        return {
          gateway: this.modelData.paymentGateway,
          accountCode: this.modelData.paymentAccountCode
        };
      }
      getErrorReporter().logAndCaptureWarning(
        'Payment gateway is Payway but account code is missing on invoice.  Recalculating...'
      );
      return null;
    }
    // NOTE: Only Payway currently requires an accountCode (sync format) to route transactions.
    // Should additional gateways introduce similar requirements, extend PaymentGatewayAndAccountCode union.
    // Non-Payway gateways never have accountCode; use undefined to satisfy discriminated union
    return {
      gateway: this.modelData.paymentGateway as Exclude<
        PaymentGateway,
        PaymentGateway.PAYWAY
      >,
      accountCode: undefined
    };
  }

  /**
   * The gateway that took this invoice's payment, or null before any payment or when it can't be
   * determined. Never consults the organization, whose gateway can change after payment.
   */
  public getPaidThroughGateway(): PaymentGateway | null {
    const {
      status,
      paymentGateway,
      paid_outside_stripe,
      invoiceOutsideColumn
    } = this.modelData;
    if (INVOICE_STATUSES_UNPAID.includes(status)) {
      return null;
    }
    if (paymentGateway) {
      return paymentGateway;
    }
    if (!paid_outside_stripe && !invoiceOutsideColumn) {
      return PaymentGateway.STRIPE;
    }
    return null;
  }

  /**
   * Fetch gateway info for this invoice. If already cached on the invoice return it immediately.
   * If it was paid through Stripe, return Stripe.
   * Otherwise calculate as follows and persist back to the invoice for future fast access:
   *  - Order invoices: inspect first newspaper order's newspaper to determine gateway + (if Payway) sync format.
   *  - Public notice invoices: derive from organization
   * Unsupported types error.
   */
  public async calculatePaymentGatewayInfo(): Promise<
    ResponseOrError<PaymentGatewayAndAccountCode>
  > {
    const logData = {
      invoiceId: this.id,
      orderId: this.isOrderInvoice() ? this.modelData.order.id : 'undefined',
      noticeId: this.isPublicNoticeInvoice()
        ? this.modelData.noticeId
        : 'undefined',
      organizationId: this.isPublicNoticeInvoice()
        ? this.modelData.organization?.id
        : 'undefined'
    };
    const cached = this.getPaymentGatewayInfo();
    if (cached) {
      return wrapSuccess(cached);
    }
    if (this.getPaidThroughGateway() === PaymentGateway.STRIPE) {
      return wrapSuccess({
        gateway: PaymentGateway.STRIPE,
        accountCode: undefined
      });
    }
    if (this.isOrderInvoice()) {
      const [orderError, order] = await this.getOrder();
      if (orderError) {
        return wrapError(orderError);
      }
      const [newspaperOrderError, newspaperOrders] = await safeAsync(() =>
        order.getNewspaperOrders()
      )();
      if (newspaperOrderError) {
        getErrorReporter().logAndCaptureError(
          ColumnService.PAYMENTS,
          newspaperOrderError,
          'Unable to get newspaper orders for order invoice when retrieving gateway info',
          logData
        );
        return wrapError(newspaperOrderError);
      }
      const firstNo = newspaperOrders.at(0);
      if (!firstNo) {
        const err = new Error(
          'No newspaper orders found for order invoice when retrieving gateway info'
        );
        getErrorReporter().logAndCaptureError(
          ColumnService.PAYMENTS,
          err,
          'No newspaper orders found for order invoice when retrieving gateway info',
          logData
        );
        return wrapError(err);
      }
      const [gatewayError, response] = await safeAsync(() =>
        getOrganizationGatewayAndAccountCode(
          this.ctx,
          firstNo.modelData.newspaper
        )
      )();
      if (gatewayError) {
        getErrorReporter().logAndCaptureError(
          ColumnService.PAYMENTS,
          gatewayError,
          'Unable to get organization gateway for order invoice when retrieving gateway info',
          logData
        );
        return wrapError(gatewayError);
      }
      const gatewayUpdates: Partial<OrderInvoice> = {
        paymentGateway: response.gateway
      };
      if (response.gateway === PaymentGateway.PAYWAY) {
        gatewayUpdates.paymentAccountCode = response.accountCode;
      }
      const [persistErr] = await safeUpdateModel(this, gatewayUpdates);
      if (persistErr) {
        getErrorReporter().logAndCaptureError(
          ColumnService.PAYMENTS,
          persistErr,
          'Failed to persist calculated gateway info (order invoice)',
          logData
        );
      }
      return wrapSuccess(response);
    }
    // Non-order invoice path
    if (this.isPublicNoticeInvoice()) {
      const [gatewayError, response] = await safeAsync(() =>
        getOrganizationGatewayAndAccountCode(
          // TS can't track the type narrowing in the lambda, so we assert something sensical here
          (this as InvoiceModel<PublicNoticeInvoice>).ctx,
          (this as InvoiceModel<PublicNoticeInvoice>).modelData.organization
        )
      )();
      if (gatewayError) {
        getErrorReporter().logAndCaptureError(
          ColumnService.PAYMENTS,
          gatewayError,
          'Unable to get organization gateway for non-order invoice when retrieving gateway info',
          logData
        );
        return wrapError(gatewayError);
      }
      const gatewayUpdates: Partial<PublicNoticeInvoice> = {
        paymentGateway: response.gateway
      };
      if (response.gateway === PaymentGateway.PAYWAY) {
        gatewayUpdates.paymentAccountCode = response.accountCode;
      }
      // Cast needed inside lambda; TS loses the branch narrowing to PublicNoticeInvoice
      const [persistErr] = await safeAsync(() =>
        (this as InvoiceModel<PublicNoticeInvoice>).update(gatewayUpdates)
      )();
      if (persistErr) {
        getErrorReporter().logAndCaptureError(
          ColumnService.PAYMENTS,
          persistErr,
          'Failed to persist calculated gateway info (public notice invoice)',
          logData
        );
      }
      return wrapSuccess(response);
    }
    const err = new Error(
      'Cannot determine payment gateway for invoice - unsupported invoice type'
    );
    getErrorReporter().logAndCaptureError(
      ColumnService.PAYMENTS,
      err,
      'Cannot determine payment gateway for invoice - unsupported invoice type',
      logData
    );
    return wrapError(err);
  }
}
